import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Instagram, 
  Loader2, 
  Check, 
  AlertTriangle, 
  ArrowLeft, 
  ChevronRight,
  ShieldCheck
} from 'lucide-react';
import { getInstagramAccounts, selectInstagramAccount } from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import '../auth/AuthForms.scss';

const InstagramAccountSelect = () => {
  const { t } = useTranslation(['creator', 'auth', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [sessionKey, setSessionKey] = useState(null);
  const [returnUrl, setReturnUrl] = useState('/creator/pending-approval');
  const [selecting, setSelecting] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const { isAuthenticated, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || !currentUser || currentUser.user_type !== 'creator') {
      navigate('/');
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const sessionKeyParam = queryParams.get('session_key');
    
    if (!sessionKeyParam) {
      setError(t('verification.errors.session_error') || 'No session key provided');
      setLoading(false);
      return;
    }
    
    setSessionKey(sessionKeyParam);
    
    const fetchAccounts = async () => {
      try {
        const response = await getInstagramAccounts(sessionKeyParam);
        const data = response.data;
        setAccounts(data.instagram_accounts || []);
        if (data.return_url) {
          setReturnUrl(data.return_url);
        }
      } catch (err) {
        setError(t('verification.errors.fetch_accounts_error') || 'Failed to load Instagram accounts.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccounts();
  }, [isAuthenticated, currentUser, navigate, location.search, t]);

  const handleSelectAccount = async (account) => {
    if (!sessionKey) return;
    
    setSelecting(true);
    setSelectedAccount(account.instagram_business_id);
    
    try {
      const response = await selectInstagramAccount(sessionKey, account.instagram_business_id);
      const data = response.data;
      
      if (data.return_url) {
        window.location.href = data.return_url;
      } else {
        navigate('/creator/pending-approval?instagram_connected=true');
      }
    } catch (err) {
      setError(t('verification.errors.select_account_error') || 'Failed to connect account.');
      setSelecting(false);
      setSelectedAccount(null);
    }
  };


  if (loading) {
    return (
      <div className="auth-loading">
        <motion.div 
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="loading-logo"
        >
          FOTH
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-page editorial-split">
      {/* Editorial Side */}
      <div className="auth-editorial-side">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="editorial-bg-overlay"
            style={{ backgroundImage: "url('/creators/creator_bg_2.jpg')" }}
          />
          <div className="editorial-content">
            <Link to="/" className="auth-logo-inverse">FOTH</Link>
            <div className="editorial-text">
               <motion.h1
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
               >
                 {t('verification.instagram.title') || 'Identity Verified'}
               </motion.h1>
               <motion.p
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ delay: 0.3 }}
               >
                 {t('verification.instagram.editorial_text') || 'Connect your digital identity to start your journey with the world\'s most exclusive brands.'}
               </motion.p>
            </div>
          </div>
      </div>

      {/* Form Side */}
      <div className="auth-form-side">
        <div className="auth-container">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="auth-card-modern"
          >
            <div className="auth-header">
               <button onClick={() => navigate(returnUrl)} className="back-link">
                  <ArrowLeft size={16} /> {t('common:buttons.back')}
               </button>
               <h2 className="auth-title">
                  {t('verification.instagram.select_title') || 'Select Instagram Account'}
               </h2>
               <p className="auth-subtitle">
                  {t('verification.instagram.select_subtitle') || 'Choose the professional account you want to link.'}
               </p>
            </div>

            <div className="auth-body">
              <AnimatePresence>
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="alert-premium error mb-6"
                  >
                    <AlertTriangle size={18} /> {error}
                  </motion.div>
                )}
              </AnimatePresence>
              
              {accounts.length === 0 ? (
                <div className="alert-premium warning">
                  <AlertTriangle size={18} /> 
                  <p>{t('verification.instagram.no_accounts') || 'No professional accounts found connected to your Facebook pages.'}</p>
                </div>
              ) : (
                <div className="instagram-accounts-stack">
                  {accounts.map((account) => (
                    <motion.div 
                      key={account.instagram_business_id} 
                      className={`instagram-account-card-luxury ${selectedAccount === account.instagram_business_id ? 'active' : ''}`}
                      onClick={() => !selecting && handleSelectAccount(account)}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="account-icon-wrap">
                        <Instagram size={20} />
                      </div>
                      <div className="account-details">
                        <h4>@{account.instagram_username}</h4>
                        <p>{account.page_name}</p>
                      </div>
                      <div className="account-action-indicator">
                        {selecting && selectedAccount === account.instagram_business_id ? (
                          <Loader2 className="spin" size={18} />
                        ) : selectedAccount === account.instagram_business_id ? (
                          <Check size={18} className="text-success" />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            <div className="auth-footer">
               <div className="safety-badge-elite">
                  <ShieldCheck size={14} /> {t('verification.documents.safety_badge')}
               </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default InstagramAccountSelect;
 
